import ReturnHeaderMeta from "@dana/Components/StandardReturns/ReturnHeaderMeta";
import { FreightCollectInfo } from "@dana/Services/DanaStandardReturnOrderService";
import ApplicationStateDana from "@dana/Store/DanaApplicationState";

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import translate from "@insite/client-framework/Translate";
import { CountryModel, ShipViaDto, StateModel } from "@insite/client-framework/Types/ApiModels";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const blank: FreightCollectInfo = {
    customerName: "",
    country: "",
    zipCode: "",
    state: "",
    city: "",
    address1: "",
    address2: "",
    freightHandling: "",
    carrier: "",
    carrierAccountNo: "",
    shippingInstructions: "",
    internalRefNo: "",
};

// --- Props Interface ---
interface StepFreightCollectProps {
    setFreight: (f: FreightCollectInfo) => void;
    onChangeInternalRef: (v: string) => void;
}

// --- Styles Interfaces and Objects ---
export interface StepFreightCollectStyles {
    wrapper?: InjectableCss;
    gridWrapper?: InjectableCss;
    inputGroup?: InjectableCss;
    sectionTitle?: TypographyPresentationProps;
    subText?: TypographyPresentationProps;
}

export const StepFreightCollectStyles: StepFreightCollectStyles = {
    wrapper: {
        css: css`
            .internal-ref {
                margin-bottom: 32px;
            }
        `,
    },
    gridWrapper: {
        css: css`
            display: grid;
            grid-template-columns: 1fr;
            gap: 16px;
            margin-bottom: 32px;

            @media only screen and (min-width: 767px) {
                grid-template-columns: repeat(2, 1fr);
                gap: 32px;
            }
        `,
    },
    sectionTitle: {
        css: css`
            margin-bottom: 16px;
        `,
    },
    subText: {
        css: css`
            margin-top: 4px;
        `,
    },
};

const styles = StepFreightCollectStyles;
const mapStateToProps = (state: ApplicationStateDana) => {
    const countries = getCurrentCountries(state);
    return {
        order: state.standardReturnState?.selectedOrder,
        freight: state.standardReturnState?.freightCollectInfo,
        internalRefNumber: state.standardReturnState?.internalRefNumber,
        carrier: state.standardReturnState?.carrier,
        countries
    };
};
type Props = ReturnType<typeof mapStateToProps> &
    StepFreightCollectProps;
// --- Component Definition ---
const StepFreightCollect: FC<Props> = ({ carrier, countries, freight, internalRefNumber, setFreight, onChangeInternalRef }) => {
 
    const [selectedCountry, setSelectedCountry] = useState<CountryModel>();
    useEffect(() => {
        setFreight({ ...freight!, freightHandling: carrier?.id || "" })
    }, [carrier])


    const setFreightCountryHandler = (countryId: string) => {
        const mappedCountry = countries?.find(x => x.id === countryId);
        setSelectedCountry(mappedCountry);
        if (mappedCountry) {
            setFreight({ ...freight!, country: mappedCountry?.name, state: "" })
        }
    }
    return (
        <StyledWrapper {...styles.wrapper}>
            <ReturnHeaderMeta />
            {/* Address Grid */}
            <StyledWrapper className="address-grid" {...styles.gridWrapper}>
                <StyledWrapper className="input-group">
                    <TextField
                        value={freight?.customerName}
                        onChange={e => setFreight({ ...freight!, customerName: e.target.value })}
                        label={translate("Customer Name")}
                        placeholder={translate("Enter Customer Name")}
                        required
                    />
                </StyledWrapper>
                <StyledWrapper className="input-group">
                    <Select
                        value={selectedCountry?.id}
                        onChange={e => setFreightCountryHandler(e.target.value)}
                        required
                        label={translate("Country")}
                    >
                        <option value="">{translate("Select")}</option>
                        {countries?.map((country: CountryModel) => (
                            <option key={country.id} value={country.id}>{country.name}</option>
                        ))}
                    </Select>
                </StyledWrapper>
                <StyledWrapper className="input-group">
                    <TextField
                        value={freight?.zipCode}
                        onChange={e => setFreight({ ...freight!, zipCode: e.target.value })}
                        label={translate("Zip / Postal Code")}
                        placeholder={translate("Enter Zip / Postal Code")}
                        required
                    />
                </StyledWrapper>
                <StyledWrapper className="input-group">
                    {selectedCountry?.states?.length ? (

                        <Select
                            value={freight?.state}
                            onChange={e => setFreight({ ...freight!, state: e.target.value })}
                            required
                            label={translate("State/Province/Region")}
                        >
                            <option value="">{translate("Select")}</option>
                            {selectedCountry?.states?.map((state: StateModel) => (
                                <option key={state.id} value={state.name}>{state.name}</option>
                            ))}
                        </Select>
                    ) : (
                        <TextField
                            value={freight?.state}
                            onChange={e => setFreight({ ...freight!, state: e.target.value })}
                            label={translate("State/Province/Region")}
                            placeholder={translate("State/Province/Region")}
                            required
                        />
                    )}
                </StyledWrapper>
                <StyledWrapper className="input-group">
                    <TextField
                        value={freight?.city}
                        onChange={e => setFreight({ ...freight!, city: e.target.value })}
                        label={translate("City")}
                        placeholder={translate("Enter City")}
                        required
                    />
                </StyledWrapper>
                <StyledWrapper className="input-group">
                    <TextField
                        value={freight?.address1}
                        onChange={e => setFreight({ ...freight!, address1: e.target.value })}
                        label={translate("Address Line 1")}
                        placeholder={translate("Apartment, Suite, Unit, Building, Floor... etc")}
                        required
                    />
                    <Typography {...styles.subText}>{translate("Street address, P.O. box, c/o")}</Typography>
                </StyledWrapper>
                <StyledWrapper className="input-group">
                    <TextField
                        value={freight?.address2}
                        onChange={e => setFreight({ ...freight!, address2: e.target.value })}
                        label={translate("Address Line 2")}
                        placeholder={translate("Street Address, P.O. Box, C/O")}
                    />
                    <Typography {...styles.subText}>
                        {translate("Apartment, suite, unit, building, floor, etc.")}
                    </Typography>
                </StyledWrapper>
            </StyledWrapper>
            {/* Return Freight Collect */}
            <StyledWrapper className="return-freight-section">
                <Typography {...styles.sectionTitle} variant="h3">
                    {translate("Return Freight Collect Specifiy Shipping")}
                </Typography>
                <StyledWrapper {...styles.gridWrapper}>
                    <StyledWrapper className="input-group">
                        {carrier && (<Select
                            value={freight?.freightHandling}
                            onChange={e => setFreight({ ...freight!, freightHandling: e.target.value })}
                            required
                            label={translate("Freight Handling")}
                        >
                            <option value="">{translate("Select")}</option>
                            <option key={carrier.id || ""} value={carrier.id || ""}>{carrier.description}</option>
                        </Select>)}

                    </StyledWrapper>
                    <StyledWrapper className="input-group">
                        <Select
                            value={freight?.carrier}
                            onChange={e => setFreight({ ...freight!, carrier: e.target.value })}
                            required
                            label={translate("Carrier")}
                        >
                            <option value="">{translate("Select")}</option>
                            {carrier?.shipVias?.map((shipVia: ShipViaDto) => (
                                <option key={shipVia?.id} value={shipVia?.id}>{shipVia?.description}</option>
                            ))}
                        </Select>
                    </StyledWrapper>
                    <StyledWrapper className="input-group">
                        <TextField
                            value={freight?.carrierAccountNo}
                            onChange={e => setFreight({ ...freight!, carrierAccountNo: e.target.value })}
                            label={translate("Carrier Account No.")}
                            placeholder={translate("Enter Carrier Account No.")}
                            required
                        />
                    </StyledWrapper>
                    <StyledWrapper className="input-group">
                        <TextField
                            value={freight?.shippingInstructions}
                            onChange={e => setFreight({ ...freight!, shippingInstructions: e.target.value })}
                            label={translate("Shipping Instructions (optional)")}
                            placeholder={translate("Enter Shipping Instructions")}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </StyledWrapper>
            {/* Internal Ref. Section */}
            <StyledWrapper className="internal-ref">
                <Typography {...styles.sectionTitle} variant="h3">
                    {translate("Your Internal Return Reference Number (optional)")}
                </Typography>
                <StyledWrapper className="input-group">
                    <TextField
                        value={internalRefNumber}
                        onChange={e => onChangeInternalRef(e.target.value)}
                        label={translate("Incluce your internal return reference number (optional)")}
                        placeholder={translate("Enter Reference Number")}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(StepFreightCollect);
